import { useEffect, useRef } from "react";
import { connectSocket, onSocketEvent } from "../socket/socketClient";
import {
  ORDER_SOCKET_EVENTS,
  extractOrderIdsFromSocketPayload,
} from "../socket/orderSocketEvents";

/**
 * True when a socket payload refers to the order currently open on details.
 * URL param is ShopifyId (e.g. /orders/order-details/7204548477160).
 */
export function socketPayloadMatchesShopifyId(payload, shopifyId) {
  if (shopifyId == null || shopifyId === "") return false;
  const target = String(shopifyId);
  const ids = extractOrderIdsFromSocketPayload(payload);
  if (ids.includes(target)) return true;

  const direct = payload?.ShopifyId ?? payload?.shopifyId ?? payload?.data?.ShopifyId;
  return direct != null && String(direct) === target;
}

/**
 * Listen for order:created / order:updated for the open Shopify order
 * and invoke onMatch (typically a silent details refetch).
 * Does not disconnect the shared socket on unmount (logout handles that).
 */
export function useOrderDetailsSocket({
  shopifyId,
  enabled = true,
  onMatch,
} = {}) {
  const onMatchRef = useRef(onMatch);
  const debounceRef = useRef(null);
  const acceptEventsRef = useRef(false);

  useEffect(() => {
    onMatchRef.current = onMatch;
  }, [onMatch]);

  useEffect(() => {
    if (!enabled || shopifyId == null || shopifyId === "") {
      acceptEventsRef.current = false;
      return undefined;
    }

    const socket = connectSocket();
    if (!socket) return undefined;

    acceptEventsRef.current = false;

    const enableAccept = () => {
      acceptEventsRef.current = true;
    };

    const schedule = (payload) => {
      if (!acceptEventsRef.current) return;
      if (!socketPayloadMatchesShopifyId(payload, shopifyId)) return;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        onMatchRef.current?.(payload);
      }, 200);
    };

    const handleConnect = () => {
      window.setTimeout(enableAccept, 200);
    };

    if (socket.connected) {
      handleConnect();
    }
    socket.on("connect", handleConnect);
    socket.on("reconnect", enableAccept);

    const unsubs = [
      onSocketEvent(ORDER_SOCKET_EVENTS.CREATED, schedule),
      onSocketEvent(ORDER_SOCKET_EVENTS.UPDATED, schedule),
    ];

    return () => {
      acceptEventsRef.current = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      socket.off("connect", handleConnect);
      socket.off("reconnect", enableAccept);
      unsubs.forEach((off) => off());
      // Do not disconnectSocket() — shared with list / auth logout.
    };
  }, [enabled, shopifyId]);
}
